export type CardProvider = "VISA" | "MASTERCARD" | "AMEX" | "DISCOVER" | "OTHER";
export type CardType = "CREDIT" | "DEBIT" | "PREPAID";
export type CardStatus = "ACTIVE" | "FROZEN" | "DISABLED" | "DELETED" | "PENDING_REVIEW";
export type CardProduct = "VIRTUAL" | "PHYSICAL";

/**
 * Normalizes API value to CardProvider type
 * Accepts uppercase, lowercase, or mixed case values
 * 
 * Usage example when receiving data from API:
 * ```typescript
 * const cardFromApi = { provider: "visa", type: "credit", status: "habilitada", product: "virtual" };
 * const normalizedCard = {
 *   ...cardFromApi,
 *   provider: normalizeProvider(cardFromApi.provider), // "VISA"
 *   type: normalizeType(cardFromApi.type),             // "CREDIT"
 *   status: normalizeStatus(cardFromApi.status),       // "HABILITADA"
 *   product: normalizeProduct(cardFromApi.product),    // "VIRTUAL"
 * };
 * ```
 */
export const normalizeProvider = (provider: string | undefined | null): CardProvider => {
  if (!provider || typeof provider !== "string") {
    return "OTHER";
  }
  
  const normalized = provider.toUpperCase();
  const validProviders: CardProvider[] = ["VISA", "MASTERCARD", "AMEX", "DISCOVER", "OTHER"];
  
  if (validProviders.includes(normalized as CardProvider)) {
    return normalized as CardProvider;
  }
  
  const providerMap: Record<string, CardProvider> = {
    "VISA": "VISA",
    "MASTERCARD": "MASTERCARD",
    "MASTER_CARD": "MASTERCARD",
    "AMEX": "AMEX",
    "AMERICAN_EXPRESS": "AMEX",
    "DISCOVER": "DISCOVER",
    "OTHER": "OTHER",
  };
  
  return providerMap[normalized] || "OTHER";
};

/**
 * Normalizes API value to CardType type
 * Accepts uppercase, lowercase, or mixed case values
 */
export const normalizeType = (type: string | undefined | null): CardType => {
  if (!type || typeof type !== "string") {
    return "PREPAID";
  }
  
  const normalized = type.toUpperCase().trim();
  
  if (normalized === "CREDIT" || normalized === "DEBIT" || normalized === "PREPAID") {
    return normalized as CardType;
  }
  
  const typeMap: Record<string, CardType> = {
    "CREDIT": "CREDIT",
    "CRÉDITO": "CREDIT",
    "CREDITO": "CREDIT",
    "DEBIT": "DEBIT",
    "DÉBITO": "DEBIT",
    "DEBITO": "DEBIT",
    "PREPAID": "PREPAID",
    "PREPAGA": "PREPAID",
    "PRE_PAID": "PREPAID",
    "PRE-PAID": "PREPAID",
    "PREPAID_CARD": "PREPAID",
  };
  
  return typeMap[normalized] || "PREPAID";
};

/**
 * Normalizes API value to CardStatus type
 * Accepts uppercase, lowercase, or mixed case values
 */
export const normalizeStatus = (status: string | undefined | null): CardStatus => {
  if (!status || typeof status !== "string") {
    return "ACTIVE";
  }
  
  const normalized = status.toUpperCase();
  const validStatuses: CardStatus[] = ["ACTIVE", "FROZEN", "DISABLED", "DELETED", "PENDING_REVIEW"];
  
  if (validStatuses.includes(normalized as CardStatus)) {
    return normalized as CardStatus;
  }
  
  const statusMap: Record<string, CardStatus> = {
    "ACTIVE": "ACTIVE",
    "FROZEN": "FROZEN",
    "DISABLED": "DISABLED",
    "DELETED": "DELETED",
    "PENDING_REVIEW": "PENDING_REVIEW",
    "PENDING REVIEW": "PENDING_REVIEW",
    "PENDINGREVIEW": "PENDING_REVIEW",
    "HABILITADA": "ACTIVE",
    "ENABLED": "ACTIVE",
    "ACTIVA": "ACTIVE",
    "DESHABILITADA": "DISABLED",
    "INACTIVE": "DISABLED",
    "INACTIVA": "DISABLED",
  };
  
  return statusMap[normalized] || "ACTIVE";
};

/**
 * Normalizes API value to CardProduct type
 * Accepts uppercase, lowercase, or mixed case values
 */
export const normalizeProduct = (product: string | undefined | null): CardProduct => {
  if (!product || typeof product !== "string") {
    return "VIRTUAL";
  }
  
  const normalized = product.toUpperCase();
  const validProducts: CardProduct[] = ["VIRTUAL", "PHYSICAL"];
  
  if (validProducts.includes(normalized as CardProduct)) {
    return normalized as CardProduct;
  }
  
  const productMap: Record<string, CardProduct> = {
    "VIRTUAL": "VIRTUAL",
    "PHYSICAL": "PHYSICAL",
    "FÍSICA": "PHYSICAL",
    "FISICA": "PHYSICAL",
  };
  
  return productMap[normalized] || "VIRTUAL";
};

export const formatProvider = (provider: CardProvider): string => {
  const providerLabels: Record<CardProvider, string> = {
    VISA: "Visa",
    MASTERCARD: "Mastercard",
    AMEX: "Amex",
    DISCOVER: "Discover",
    OTHER: "Other",
  };
  
  return providerLabels[provider] || provider;
};

export const formatType = (type: CardType): string => {
  const typeLabels: Record<CardType, string> = {
    CREDIT: "Crédito",
    DEBIT: "Débito",
    PREPAID: "Prepaga",
  };
  
  return typeLabels[type] || type;
};

export const formatStatus = (status: CardStatus): string => {
  const statusLabels: Record<CardStatus, string> = {
    ACTIVE: "Activa",
    FROZEN: "Congelada",
    DISABLED: "Deshabilitada",
    DELETED: "Eliminada",
    PENDING_REVIEW: "Pendiente de revisión",
  };
  
  return statusLabels[status] || status;
};

export const formatProduct = (product: CardProduct): string => {
  const productLabels: Record<CardProduct, string> = {
    VIRTUAL: "Virtual",
    PHYSICAL: "Física",
  };
  
  return productLabels[product] || product;
};
